import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { GifResultado } from "../../../services/v2/prospects.service";
import { abrirArchivo } from "../../../components/enrollment/visorArchivo";
import WappIcono from "../../../components/wappIcono";
import { esTactil } from "./equipoEmojis";
import "./equipoGifs.css";

/**
 * GIFs y emojis de Cliq en WappTeam (Esteban, 2026-10-11: "los GIFs no se ven; muchos mensajes de asesoras solo muestran
 * el texto del gif. Que puedan mandar GIFs, lo más parecido a Cliq").
 *
 * - Un GIF es un mensaje cuyo texto es la liga del GIF (así llegó el historial de Cliq: ~17,700 mensajes con una liga de
 *   media.tenor.com o mediaN.giphy.com; y así los manda el buscador nuevo, con ligas de static.klipy.com). Aquí se pinta
 *   animado, del tamaño de una foto de WhatsApp. Si la liga ya no carga, se ve la liga como antes.
 * - Emojis de Cliq: el historial trae códigos como :fireworks: o :gold-medal!: (el "!" era la versión animada); se cambian
 *   por el emoji normal. Los emojis PROPIOS de la organización (:carla:, :Prapra:…) eran imágenes que vivían en Zoho y no
 *   se rescataron: quedan como una etiqueta discreta en vez de texto suelto.
 * - El buscador (tendencias + búsqueda) pide al back (/wappchat/_equipoGifs), que guarda la llave de KLIPY.
 */

/** Hosts de GIF que se pintan como imagen (misma lista que App\Services\EquipoGifs::HOSTS). */
const HOSTS = "(?:media\\d*\\.tenor\\.com|c\\.tenor\\.com|media\\d*\\.giphy\\.com|i\\.giphy\\.com|static\\.klipy\\.com|[a-z0-9-]+\\.klipy\\.com)";
const GIF_EN_TEXTO = new RegExp("https://" + HOSTS + "/[^\\s<]+", "i");
/** Vista previa (el back corta el texto a 90 letras, así que la liga puede venir incompleta). */
const EMPIEZA_CON_GIF = new RegExp("^\\s*https://" + HOSTS + "/", "i");

/** Si el mensaje trae un GIF: su liga y el resto del texto (sin la liga). */
export const gifDe = (body: string | null | undefined): { url: string; resto: string } | null => {
	if (!body) return null;
	const m = GIF_EN_TEXTO.exec(body);
	if (!m) return null;
	return { url: m[0], resto: (body.slice(0, m.index) + body.slice(m.index + m[0].length)).trim() };
};

// ------------------------------------------------------------------ emojis de Cliq

/** Smileys estándar de Cliq que aparecen en el historial (conteo real, 2026-10-11) → emoji Unicode. */
const CLIQ: { [k: string]: string } = {
	fireworks: "🎆", "gold-medal": "🥇", "silver-medal": "🥈", "bronze-medal": "🥉", love: "😍", biceps: "💪", cool: "😎", fire: "🔥",
	namaste: "🙏", "new-year": "🎉", clap: "👏", search: "🔍", thinking: "🤔", smile: "😊", super: "👌", sad: "😢", joy: "😂",
	anxious: "😰", food: "🍔", grinning: "😀", "stressed-out": "😫", "feeling-warm": "🥰", surprise: "😮", thumbsup: "👍",
	thumbsdown: "👎", curious: "🧐", blush: "☺️", happy: "😄", upset: "😠", champagne: "🍾", doubt: "🤨", "woman-running": "🏃‍♀️",
	"man-running": "🏃‍♂️", "gift-box": "🎁", tired: "😩", idea: "💡", worry: "😟", wink: "😉", smirk: "😏", tensed: "😬", peace: "✌️",
	awe: "🤩", relaxed: "😌", headache: "🤕", yummy: "😋", task: "✅", sick: "🤒", injured: "🤕", facepalm: "🤦", bored: "😑",
	x: "❌", party: "🥳", faint: "😵", "eid-mubarak": "🌙", sun: "☀️", yoyo: "🪀", heart: "❤️", kiss: "😘", cry: "😭", angry: "😡",
	laugh: "😆", star: "⭐", ok: "👌", rocket: "🚀", trophy: "🏆", crown: "👑", money: "💰", cake: "🎂", coffee: "☕", gift: "🎁",
	mexico: "🇲🇽", honduras: "🇭🇳", "el-salvador": "🇸🇻", guatemala: "🇬🇹", usa: "🇺🇸",
};
const TONOS = ["", "🏻", "🏼", "🏽", "🏾", "🏿"];
const CODIGO_RE = /:([a-z][a-z0-9]*(?:-[a-z0-9]+)*)(!?):/g;
const unicodeDe = (nombre: string): string | null => {
	if (CLIQ[nombre]) return CLIQ[nombre];
	const t = /^(.+)-tone([1-5])$/.exec(nombre);
	return t && CLIQ[t[1]] ? CLIQ[t[1]].replace(/️/g, "") + TONOS[+t[2]] : null;
};
/** Cambia los códigos de smileys estándar de Cliq por el emoji (lo demás queda igual). */
export const conEmojisCliq = (t: string): string => (t.indexOf(":") < 0 ? t : t.replace(CODIGO_RE, (x, n: string) => unicodeDe(n) || x));

/**
 * Emojis propios de la organización en Cliq (imágenes que no se rescataron): ":carla:" o el código completo
 * ":0100…_885357303_3_1_nombre$:" que el importador no alcanzó a limpiar. Grupo 1 = nombre corto.
 */
// Sin lookbehind (Safari viejo de iPhone no lo entiende y tumbaría la app): el carácter de antes va en el grupo 1.
const CLIQ_PROPIO_RE = /(^|[^\w:])(?::\d{10,}_\d+_\d+_\d+_([^:\s]*?)!?\$:|:([A-Za-z][A-Za-z0-9_-]{0,39}):)(?=$|[^\w])/g;
export const partirEmojisPropios = (t: string): React.ReactNode[] => {
	if (t.indexOf(":") < 0) return [t];
	const out: React.ReactNode[] = [];
	const re = new RegExp(CLIQ_PROPIO_RE.source, "g");
	let i = 0;
	let m: RegExpExecArray | null;
	while ((m = re.exec(t))) {
		const ini = m.index + m[1].length;
		const nombre = (m[2] ?? m[3] ?? "").replace(/[_-]+/g, " ").trim();
		if (ini > i) out.push(t.slice(i, ini));
		out.push(<span key={ini} className="eq-emoji-cliq" title="Emoji de Cliq (la imagen se quedó en Zoho)">{nombre || "emoji"}</span>);
		i = m.index + m[0].length;
		re.lastIndex = i;
	}
	if (i < t.length) out.push(t.slice(i));
	return out;
};

/** Para vistas previas, citas y avisos: "GIF" en vez de la liga, y los emojis de Cliq ya cambiados. */
export const resumenTexto = (t: string | null | undefined): string => {
	if (!t) return "";
	if (EMPIEZA_CON_GIF.test(t)) {
		const g = gifDe(t);
		return g && g.resto ? "GIF · " + conEmojisCliq(g.resto) : "GIF";
	}
	// El servidor corta la vista previa a 90 letras: un "[[drive:65494" sin cerrar no debe verse crudo (Esteban, 2026-10-22).
	return conEmojisCliq(t).replace(/:\d{10,}_\d+_\d+_\d+_([^:\s]*?)!?\$:/g, ":$1:").replace(/\[\[\w+:[^\]]*$/, "");
};

// ------------------------------------------------------------------ el GIF dentro de la burbuja

export const GifMensaje: React.FC<{ url: string }> = ({ url }) => {
	const [mal, setMal] = useState(false);
	const [listo, setListo] = useState(false);
	if (mal) return <p className="eqm-gif-mal"><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></p>;
	const ver = () => abrirArchivo({
		nombre: "GIF", mime: "image/gif", origen: "WappTeam", urlLista: url, obtenerUrl: () => Promise.resolve(url), descargar: false,
		cargar: async () => ({ blob: await (await fetch(url)).blob(), nombre: "gif.gif" }),
	});
	return (
		<button type="button" className={"eqm-gif" + (listo ? " listo" : "")} onClick={ver} aria-label="Ver GIF">
			<img src={url} alt="GIF" loading="lazy" referrerPolicy="no-referrer" onLoad={() => setListo(true)} onError={() => setMal(true)} />
			<span className="eqm-gif-marca" aria-hidden="true">GIF</span>
		</button>
	);
};

// ------------------------------------------------------------------ buscador

/**
 * Botón "GIF" + panel con buscador y tendencias. Al tocar uno, onElegir(url) lo manda como mensaje.
 * El panel se abre hacia arriba, pegado al compositor; en celular ocupa el ancho de la pantalla.
 */
export const BotonGif: React.FC<{ campusId: number; onElegir: (url: string) => Promise<void> | void; compacto?: boolean; disabled?: boolean; enBarra?: boolean }> = ({ campusId, onElegir, compacto, disabled, enBarra }) => {
	const [abierto, setAbierto] = useState(false);
	const caja = useRef<HTMLDivElement>(null);
	// Teléfono: al cerrar el panel la página vuelve exactamente a donde estaba (iPhone la recorre al enfocar el buscador).
	const scrollAntes = useRef<{ x: number; y: number } | null>(null);
	useEffect(() => {
		if (abierto) { scrollAntes.current = { x: window.scrollX, y: window.scrollY }; return; }
		const s = scrollAntes.current;
		scrollAntes.current = null;
		if (s && (window.scrollX !== s.x || window.scrollY !== s.y)) requestAnimationFrame(() => window.scrollTo(s.x, s.y));
	}, [abierto]);
	useEffect(() => {
		if (!abierto) return;
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", esc); };
	}, [abierto]);
	return (
		<div className={"eqg" + (compacto ? " compacto" : "") + (enBarra ? " en-barra" : "")} ref={caja}>
			<button type="button" className={"eqg-boton eqg-gif" + (abierto ? " activo" : "")} disabled={disabled}
				onClick={() => {
					if (!abierto && esTactil() && document.activeElement instanceof HTMLElement) document.activeElement.blur();
					setAbierto(!abierto);
				}}
				aria-label="Mandar un GIF" title="GIF" aria-expanded={abierto}>GIF</button>
			{abierto ? <PanelGifs campusId={campusId} onElegir={async (u) => { setAbierto(false); await onElegir(u); }} onCerrar={() => setAbierto(false)} /> : null}
		</div>
	);
};

const PanelGifs: React.FC<{ campusId: number; onElegir: (url: string) => void; onCerrar: () => void }> = ({ campusId, onElegir, onCerrar }) => {
	const [q, setQ] = useState("");
	const [items, setItems] = useState<GifResultado[]>([]);
	const [next, setNext] = useState<string | null>(null);
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [sinLlave, setSinLlave] = useState(false);
	const [recientes, setRecientes] = useState<GifResultado[]>([]);
	const pedido = useRef(0);
	const buscar = useRef<HTMLInputElement>(null);
	// En computadora el cursor entra directo al buscador; en el teléfono no (abriría el teclado y movería la pantalla): se ven las tendencias y quien quiera buscar toca el cuadro.
	useEffect(() => { if (!esTactil()) buscar.current?.focus({ preventScroll: true }); }, []);

	const traer = async (texto: string, pos: string | null) => {
		const n = ++pedido.current;
		setCargando(true);
		try {
			const r = (await HttpManager.getInstance().prospectsService.equipoGifs(campusId, texto, pos)).data;
			if (n !== pedido.current) return;
			setSinLlave(!r.disponible);
			if (r.recientes) setRecientes(r.recientes);
			setError(r.error || null);
			setItems((prev) => (pos ? [...prev, ...r.items.filter((x) => !prev.some((p) => p.id === x.id))] : r.items));
			setNext(r.next);
		} catch (e) {
			if (n === pedido.current) setError("No se pudieron traer los GIFs. Intenta de nuevo.");
		} finally {
			if (n === pedido.current) setCargando(false);
		}
	};
	// Tendencias al abrir; búsqueda 350 ms después de dejar de escribir.
	useEffect(() => {
		const t = setTimeout(() => traer(q.trim(), null), q.trim() ? 350 : 0);
		return () => clearTimeout(t);
	}, [q]);

	const alBajar = (e: React.UIEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		if (next && !cargando && el.scrollTop + el.clientHeight > el.scrollHeight - 160) traer(q.trim(), next);
	};

	const celda = (g: GifResultado) => (
		<button key={g.id} type="button" className="eqg-item" onClick={() => onElegir(g.url)} title={g.titulo || "GIF"}
			style={g.w && g.h ? { aspectRatio: g.w + " / " + g.h } : undefined}>
			<img src={g.vista} alt={g.titulo || "GIF"} loading="lazy" referrerPolicy="no-referrer" />
		</button>
	);

	return (
		<div className="eqg-panel" role="dialog" aria-label="Buscar GIF">
			<div className="eqg-cabeza">
				<span className="eqg-buscar"><WappIcono n="search" />
					<input ref={buscar} type="search" enterKeyHint="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar GIF" aria-label="Buscar GIF" maxLength={80} />
				</span>
				<button type="button" className="eqg-cerrar" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button>
			</div>
			<div className="eqg-rejilla" onScroll={alBajar}>
				{!q.trim() && recientes.length ? (
					<>
						<div className="eqg-titulo">Recientes</div>
						<div className="eqg-columnas">{recientes.map((g) => celda(g))}</div>
					</>
				) : null}
				<div className="eqg-titulo">{q.trim() ? "Resultados" : "Tendencias"}</div>
				{sinLlave ? <div className="eqg-vacio">Los GIFs todavía no están activados. Falta poner la llave de KLIPY en el servidor.</div>
					: error && !items.length ? <div className="eqg-vacio">{error}</div>
					: !cargando && !items.length ? <div className="eqg-vacio">No hay GIFs para “{q.trim()}”.</div>
					: (
						<div className="eqg-columnas">
							{items.map((g) => celda(g))}
						</div>
					)}
				{cargando ? <div className="eqg-cargando" aria-label="Cargando" /> : null}
			</div>
			<div className="eqg-pie">Búsqueda con KLIPY</div>
		</div>
	);
};
